import { Link } from 'react-router-dom'
import { useT } from '@/i18n'
import { fleetMessages } from '@/i18n/messages/fleet'
import { fleetMapMessages } from '@/i18n/messages/fleetMap'
import type { PhoneFix } from './usePhoneFix'
import type { VehicleStatus } from './types'

/** List | Map, shared by the register and the full-bleed map. */
export function ViewSwitch({ current }: { current: 'list' | 'map' }) {
  const t = useT(fleetMapMessages)
  const cls = (on: boolean) => `rounded-lg px-3 py-1.5 text-sm font-medium ${on ? 'bg-white shadow-sm dark:bg-neutral-700' : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-300'}`
  return (
    <nav className="inline-flex rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800">
      <Link to="/fleet/list" className={cls(current === 'list')} aria-current={current === 'list' ? 'page' : undefined}>{t('viewList')}</Link>
      <Link to="/fleet" className={cls(current === 'map')} aria-current={current === 'map' ? 'page' : undefined}>{t('viewMap')}</Link>
    </nav>
  )
}

const TONE: Record<VehicleStatus, string> = {
  available: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  reserved: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
  out: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
}

export function VehicleStatusBadge({ status }: { status: VehicleStatus }) {
  const t = useT(fleetMessages)
  const label = { available: t('statusAvailable'), reserved: t('statusReserved'), out: t('statusOut') }[status]
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[status]}`}>{label}</span>
}

/** TÜV as AU records it: past dates in amber (AU's dates are often not maintained), due within 60 days in red. */
export function TuvText({ due }: { due: string | null }) {
  const t = useT(fleetMessages)
  if (!due) return <span className="text-neutral-400">—</span>
  const days = Math.round((Date.parse(due) - Date.now()) / 86_400_000)
  const tone = days < 0 ? 'text-amber-700 dark:text-amber-300' : days <= 60 ? 'text-red-700 dark:text-red-300 font-semibold' : 'text-neutral-600 dark:text-neutral-300'
  const [y, m] = due.split('-')
  return <span className={`tabular ${tone}`} title={days < 0 ? t('tuvPastHint') : undefined}>{m}/{y}</span>
}

/** Fuel level in eighths, as a gauge of eight segments. */
export function FuelGauge({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  const t = useT(fleetMessages)
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1" role={onChange ? 'radiogroup' : undefined} aria-label={t('fuel')}>
        {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
          onChange ? (
            <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n}/8`} onClick={() => onChange(value === n && n === 1 ? 0 : n)}
              className={`h-9 w-5 rounded-md ring-1 ${n <= value ? 'bg-amber-500 ring-amber-500' : 'bg-neutral-100 ring-neutral-300 dark:bg-neutral-800 dark:ring-neutral-700'}`} />
          ) : (
            <span key={n} className={`h-3 w-2 rounded-sm ${n <= value ? 'bg-amber-500' : 'bg-neutral-200 dark:bg-neutral-700'}`} />
          )
        ))}
      </div>
      <span className="tabular text-sm text-neutral-600 dark:text-neutral-300">{value}/8</span>
    </div>
  )
}

/** One line under a check-out / check-in form: is the phone's position going along? */
export function FixNote({ fix }: { fix: PhoneFix }) {
  const t = useT(fleetMapMessages)
  return (
    <p className="text-xs text-neutral-500">
      📍 {fix.state === 'locating' ? t('fixLocating') : fix.state === 'ok' ? t('fixOk', { m: fix.fix.accuracyM ?? '?' }) : t('fixNone')}
    </p>
  )
}
